import { useState, useEffect } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';

/**
 * Overlay hiển thị khi server đang restart (502/503).
 * Lắng nghe custom event 'server-retry' từ axios interceptor.
 */
export default function ServerRetryOverlay() {
  const {
    t
  } = useLingui();

  const [visible, setVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [jumping, setJumping] = useState(false);

  useEffect(() => {
    let hideTimer;

    const handleRetry = (e) => {
      setAttempt(e.detail?.attempt || 1);
      setVisible(true);
      setJumping(true);
      setTimeout(() => setJumping(false), 600);
      // Auto-hide sau 2s nếu không có retry tiếp
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setVisible(false), 4000);
    };

    const handleResolved = () => {
      // Server đã phản hồi OK → ẩn overlay
      clearTimeout(hideTimer);
      setTimeout(() => setVisible(false), 800);
    };

    window.addEventListener('server-retry', handleRetry);
    window.addEventListener('server-retry-resolved', handleResolved);
    return () => {
      window.removeEventListener('server-retry', handleRetry);
      window.removeEventListener('server-retry-resolved', handleResolved);
      clearTimeout(hideTimer);
    };
  }, []);

  if (!visible) return null;

  // 1 thông báo duy nhất cho mọi lỗi tạm thời (mạng chập chờn / server bận) —
  // tránh đổ lỗi "hệ thống đang cập nhật" gây hiểu nhầm hệ thống hỏng.
  const ui = {
    bar: 'from-amber-400 via-orange-500 to-amber-400',
    spinner: 'border-amber-200 border-t-amber-600',
    title: t`Kết nối Internet không ổn định`,
    desc: t`Vui lòng kiểm tra đường truyền mạng của bạn. Hệ thống sẽ tự kết nối lại — thao tác được giữ nguyên, không mất dữ liệu.`,
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-sm w-full mx-4 overflow-hidden">
        {/* Top: animated gradient bar — clean progress indicator */}
        <div className={`h-1 bg-gradient-to-r ${ui.bar}`}
          style={{ backgroundSize: '200% 100%', animation: 'shr-shimmer 1.4s ease-in-out infinite' }} />

        <div className="px-6 py-5">
          <div className="flex items-start gap-3">
            {/* Spinner */}
            <div className="shrink-0 mt-0.5">
              <div className={`w-9 h-9 rounded-full border-2 ${ui.spinner}`}
                style={{ animation: 'shr-spin 1s linear infinite' }} />
            </div>
            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{ui.title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{ui.desc}</p>
              <p className="text-[11px] text-gray-400 mt-2"><Trans>Đang kết nối lại (lần</Trans> {attempt})…</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shr-spin { to { transform: rotate(360deg); } }
        @keyframes shr-shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}
